import { useId, useState } from 'react';
import { CopyTextButton, Link } from '@backstage/core-components';
import { Tab, Tabs, makeStyles } from '@material-ui/core';
import { StatusCard } from '../status/StatusCard';
import { Breakable } from './ServerList';

/**
 * The column beside the servers on the MCP & CLI page (specs/mcp-and-cli.md
 * AC-6 to AC-8): how to connect Claude to the portal, and the two tools a
 * colleague runs on their own machine.
 */

const useStyles = makeStyles(theme => ({
  body: {
    padding: theme.spacing(1.5, 2),
    fontSize: '0.875rem',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    minWidth: 0,
  },
  text: { margin: 0, lineHeight: 1.6 },
  muted: { margin: 0, color: 'var(--ui-text-muted)', lineHeight: 1.6 },
  command: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1, 1, 1.5),
    borderRadius: 6,
    background: 'var(--ui-bg-muted)',
    border: '1px solid var(--ui-border)',
    minWidth: 0,
  },
  // Wrapped rather than scrolled: the command is one line to copy, and a phone
  // shows it on several without pushing the page sideways.
  code: {
    flex: 1,
    margin: 0,
    fontFamily: 'var(--font-mono)',
    fontSize: '0.75rem',
    lineHeight: 1.6,
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
    minWidth: 0,
  },
  tabs: { minHeight: 40, borderBottom: '1px solid var(--ui-border)' },
  tab: { minHeight: 40, textTransform: 'none' },
  links: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5, 1.5),
  },
}));

/** Where claude.ai keeps its connectors. */
const CLAUDE_CONNECTORS = 'https://claude.ai/settings/connectors';

const TABS = [
  { id: 'claude-code', label: 'Claude Code' },
  { id: 'claude-ai', label: 'Claude.ai' },
] as const;

/**
 * The command for Claude Code names the endpoint as this portal is deployed,
 * so the one copied from a local portal reaches the local endpoint and the one
 * copied in production reaches production.
 */
export const commandFor = (address: string) =>
  `claude mcp add --transport http backstage ${address}`;

export const ConnectCard = ({ address }: { address?: string }) => {
  const classes = useStyles();
  const id = useId();
  const [tab, setTab] = useState<(typeof TABS)[number]['id']>('claude-code');
  const command = address ? commandFor(address) : undefined;

  return (
    <StatusCard id="mcp-connect" title="Connect Claude">
      <Tabs
        value={tab}
        onChange={(_, value) => setTab(value)}
        aria-label="How to connect Claude to the portal"
        indicatorColor="primary"
        className={classes.tabs}
      >
        {TABS.map(t => (
          <Tab
            key={t.id}
            value={t.id}
            label={t.label}
            className={classes.tab}
            id={`${id}-${t.id}`}
            aria-controls={`${id}-panel`}
          />
        ))}
      </Tabs>
      <div
        className={classes.body}
        role="tabpanel"
        id={`${id}-panel`}
        aria-labelledby={`${id}-${tab}`}
      >
        {tab === 'claude-code' ? (
          <>
            <p className={classes.muted}>
              Once, in a terminal. The first time Claude uses it, a browser
              opens to sign in with ZITADEL.
            </p>
            {command ? (
              <div className={classes.command}>
                <pre className={classes.code}>
                  <code>
                    <Breakable text={command} />
                  </code>
                </pre>
                <CopyTextButton
                  text={command}
                  tooltipText="Command copied"
                  aria-label="Copy the command"
                  variant="tertiary"
                />
              </div>
            ) : (
              <p className={classes.muted}>
                The portal could not say where its endpoint is.
              </p>
            )}
          </>
        ) : (
          <>
            <p className={classes.text}>
              Backstage is a connector for the whole organisation. In claude.ai,
              open <strong>Settings → Connectors</strong>, choose{' '}
              <strong>Backstage</strong> and <strong>Connect</strong>, then sign
              in with ZITADEL.
            </p>
            <Link to={CLAUDE_CONNECTORS}>Open the connectors in claude.ai</Link>
          </>
        )}
      </div>
    </StatusCard>
  );
};

/**
 * JAM, the local cloud. It names no version: the latest is a link, so the card
 * cannot fall behind a release the way a number written into it would.
 */
export const JamCard = () => {
  const classes = useStyles();
  return (
    <StatusCard id="mcp-jam" title="JAM" aside="Local cloud">
      <div className={classes.body}>
        <p className={classes.text}>
          The platform on your own machine — Traefik, CoreDNS and a CA for
          *.rvnxx.test.
        </p>
        <div className={classes.links}>
          <Link to="https://github.com/revenexx/jam/releases/latest">
            Latest release
          </Link>
          <Link to="/docs/default/component/jam">Setup guide</Link>
          <Link to="/jam">About JAM</Link>
        </div>
      </div>
    </StatusCard>
  );
};

/** The revenexx CLI, as revenexx.dev describes it. */
export const CliCard = () => {
  const classes = useStyles();
  return (
    <StatusCard id="mcp-cli" title="revenexx CLI">
      <div className={classes.body}>
        <p className={classes.text}>
          Sign in, select a tenant and script the platform's API from your
          terminal.
        </p>
        <div className={classes.links}>
          <Link to="https://revenexx.dev/docs/tools/command-line/installation">
            Install guide
          </Link>
          <Link to="https://revenexx.dev/docs/sdks/cli">CLI docs</Link>
        </div>
      </div>
    </StatusCard>
  );
};
